import React from "react";
import { CheckboxGroupState, useCheckboxGroupState } from "react-stately";
import { AriaCheckboxGroupProps, useCheckboxGroup } from "react-aria";
import { Flex, FlexProps } from "../flex";
import { Text } from "../text";
import { InputError } from "../input-error";
import { CheckboxVariantProps } from "./checkbox";

export const GroupContext = React.createContext<{
  state: CheckboxGroupState;
  props: CheckboxVariantProps;
} | null>(null);

interface CheckboxGroupProps
  extends AriaCheckboxGroupProps,
    CheckboxVariantProps {
  children?: React.ReactNode;
}

export const CheckboxGroup = React.forwardRef<
  HTMLDivElement,
  CheckboxGroupProps & FlexProps
>((props, ref) => {
  const {
    children,
    align,
    justify,
    label,
    description,
    errorMessage,
    size,
    appearance,
    color,
    shape,
  } = props;

  const state = useCheckboxGroupState(props);
  const { groupProps, labelProps, descriptionProps, errorMessageProps } =
    useCheckboxGroup(props, state);

  return (
    <Flex
      align={align}
      justify={justify}
      ref={ref}
      stack
      gap={"2xs"}
      {...groupProps}
    >
      <Text {...labelProps}>{label}</Text>
      <GroupContext.Provider
        value={{ state, props: { size, appearance, color, shape } }}
      >
        {children}
      </GroupContext.Provider>
      {description && (
        <Text
          lineHeight={"base"}
          variant="lighter"
          size="xs"
          {...descriptionProps}
        >
          {description}
        </Text>
      )}
      {errorMessage && (
        <InputError {...errorMessageProps}>{errorMessage}</InputError>
      )}
    </Flex>
  );
});

CheckboxGroup.displayName = "CheckboxGroup";
